import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Avatar,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  Stack,
  Tooltip,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  Verified,
  GitHub,
  CheckCircle,
  ArrowUpward,
  ExitToApp,
  Person,
  OpenInNew,
  CreditCard,
} from '@mui/icons-material';
import { RootState } from '../../store/store';
import { logout, updateUser } from '../../store/slices/authSlice';
import { useGetMeQuery, useCreatePortalSessionMutation } from '../../store/api/authApi';
import UpgradeModal from '../../components/upgrade/UpgradeModal';

// ── Plan metadata ─────────────────────────────────────────────────────────────

const PLAN_META: Record<string, {
  label: string;
  color: 'default' | 'primary' | 'success';
  features: string[];
  scansPerMonth: string;
  fixes: string;
  repos: string;
}> = {
  free: {
    label: 'Free',
    color: 'default',
    features: ['1 repository', '3 scans / month', 'Static analysis only', 'No AI fixes'],
    scansPerMonth: '3',
    fixes: 'None',
    repos: '1',
  },
  pro: {
    label: 'Pro',
    color: 'primary',
    features: ['5 repositories', '20 scans / month', 'Full AI analysis', '50 AI fixes / month', 'Automated PRs'],
    scansPerMonth: '20',
    fixes: '50 / mo',
    repos: '5',
  },
  enterprise: {
    label: 'Enterprise',
    color: 'success',
    features: ['Unlimited repositories', 'Unlimited scans', 'Unlimited AI fixes', 'Priority support', 'Custom integrations'],
    scansPerMonth: 'Unlimited',
    fixes: 'Unlimited',
    repos: 'Unlimited',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

export const ProfilePage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: storedUser } = useSelector((state: RootState) => state.auth);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [createPortal, { isLoading: isPortalLoading }] = useCreatePortalSessionMutation();
  const [portalError, setPortalError] = useState('');

  // Refresh user data from the server
  const { data: freshUser } = useGetMeQuery();
  const user = freshUser ?? storedUser;

  // Sync fresh data into Redux store so the rest of the app stays current
  React.useEffect(() => {
    if (freshUser) dispatch(updateUser(freshUser));
  }, [freshUser, dispatch]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    dispatch(logout());
    navigate('/auth/login');
  };

  const handleManageBilling = async () => {
    setPortalError('');
    try {
      const { portal_url } = await createPortal().unwrap();
      window.location.href = portal_url;
    } catch {
      setPortalError('Could not open billing portal. Please try again.');
    }
  };

  if (!user) return null;

  const safeName = (user.github_login ?? '').trim();
  const initial = safeName ? safeName.charAt(0).toUpperCase() : null;
  const plan = PLAN_META[user.plan] ?? PLAN_META.free;
  const displayEmail = user.email ?? user.github_email;

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 1, sm: 2 } }}>
      <Grid container spacing={3}>

        {/* ── Left column: Account & Connections (~60% width) ── */}
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 3, flexGrow: 1 }}>
              {/* Account Profile Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
                <Avatar
                  src={user.avatar_url ?? undefined}
                  sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: '1.75rem', fontWeight: 700 }}
                >
                  {initial ?? <Person sx={{ fontSize: 36 }} />}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="h5" fontWeight={800} noWrap sx={{ letterSpacing: '-0.01em' }}>
                      {safeName || 'Your Account'}
                    </Typography>
                    <Chip
                      label={plan.label}
                      color={plan.color}
                      size="small"
                      sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                    />
                  </Box>

                  {displayEmail && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" color="text.secondary" noWrap fontWeight={500}>
                        {displayEmail}
                      </Typography>
                      {user.email_verified ? (
                        <Tooltip title="Email verified">
                          <Verified sx={{ fontSize: 16, color: 'success.main' }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Email not verified">
                          <Verified sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              {/* Connections details */}
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block', mb: 1.5 }}>
                Account Connections
              </Typography>

              <Stack divider={<Divider />} spacing={0}>
                <Row label="GitHub" value={
                  safeName
                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <GitHub sx={{ fontSize: 16, color: 'text.primary' }} />
                      <Typography variant="body2" fontWeight={600}>@{safeName}</Typography>
                    </Box>
                    : <Typography variant="body2" color="text.disabled">Not connected</Typography>
                } />
                <Row label="Email" value={
                  displayEmail
                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" fontWeight={500}>{displayEmail}</Typography>
                      {user.email_verified
                        ? <Chip label="Verified" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
                        : <Chip label="Unverified" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
                      }
                    </Box>
                    : <Typography variant="body2" color="text.disabled">—</Typography>
                } />
                <Row label="GitHub App" value={
                  user.installation_id
                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="body2" fontWeight={600} color="success.main">Connected</Typography>
                    </Box>
                    : <Typography variant="body2" color="warning.main" fontWeight={600}>Not installed</Typography>
                } />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Right column: Plan, Billing & Sign Out (~40% width) ── */}
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>

            {/* Plan Card */}
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.7rem' }}>
                    Plan
                  </Typography>
                  {user.plan === 'free' && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ArrowUpward sx={{ fontSize: 14 }} />}
                      onClick={() => setUpgradeOpen(true)}
                      sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, fontSize: '0.75rem', py: 0.5 }}
                    >
                      Upgrade to Pro
                    </Button>
                  )}
                </Box>

                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <StatBox label="Repositories" value={plan.repos} />
                  </Grid>
                  <Grid item xs={4}>
                    <StatBox label="Scans / mo" value={plan.scansPerMonth} />
                  </Grid>
                  <Grid item xs={4}>
                    <StatBox label="Fixes / mo" value={plan.fixes} />
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 1.5 }} />

                <Stack spacing={0.75}>
                  {plan.features.map((f) => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
                      <Typography variant="caption" color="text.primary" fontWeight={500}>{f}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Billing Card (Pro / Enterprise only) */}
            {user.plan !== 'free' && (
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block' }}>
                    Billing
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CreditCard sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500} lineHeight={1.4}>
                      Manage your subscription, update payment method, or download invoices.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    startIcon={isPortalLoading ? <CircularProgress size={12} /> : <OpenInNew sx={{ fontSize: 14 }} />}
                    onClick={handleManageBilling}
                    disabled={isPortalLoading}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                  >
                    {isPortalLoading ? 'Opening…' : 'Manage Billing'}
                  </Button>
                  {portalError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                      {portalError}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Sign out */}
            <Box sx={{ mt: 'auto', pt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<ExitToApp sx={{ fontSize: 16 }} />}
                onClick={handleLogout}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, px: 2.5 }}
              >
                Sign Out
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason="general" />
    </Box>
  );
};

// ── Small helper components ───────────────────────────────────────────────────

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, gap: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110, flexShrink: 0, fontWeight: 500 }}>
      {label}
    </Typography>
    {value}
  </Box>
);

const StatBox: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
    <Typography variant="subtitle1" fontWeight={800} lineHeight={1.1}>{value}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>{label}</Typography>
  </Box>
);
